import { useCallback, useMemo } from "react"
import { useQuery } from "urql"

import type { GoodsReceiptPallet } from "@/graphql/codegen/graphql"
import { GetGoodsReceiptPalletsListDocument } from "@/graphql/codegen/graphql"

interface ReturnProps {
  pallets: GoodsReceiptPallet[]
  isFetching: boolean
  error: Error | undefined
  refetch: () => void
}

export const useGetGoodsReceiptPalletsForPrint = (goodsReceiptId?: string | null): ReturnProps => {
  const [{ data: palletsData, fetching: palletsFetching, error: palletsError }, reexecutePalletsQuery] = useQuery({
    query: GetGoodsReceiptPalletsListDocument,
    variables: {
      input: {
        filter: { goodsReceiptId },
        // Large limit to fetch all pallets for printing and avoid creating multiple queries
        // typical goods receipts have not a lot of pallets
        limit: 10000,
        offset: 0,
      },
    },
    pause: !goodsReceiptId,
  })

  const refetch = useCallback(() => {
    reexecutePalletsQuery({ requestPolicy: "network-only" })
  }, [reexecutePalletsQuery])

  const pallets = useMemo<GoodsReceiptPallet[]>(() => {
    if (palletsData?.goodsReceipt.palletList.__typename !== "GoodsReceiptPalletListSuccess") {
      return []
    }

    return palletsData.goodsReceipt.palletList.goodsReceiptPallets as GoodsReceiptPallet[]
  }, [palletsData])

  return {
    pallets,
    isFetching: palletsFetching,
    error: palletsError,
    refetch,
  }
}
